/** Scene / Shot container (spec §8/§9) — a first-class group node.
 * Children (Character/Dialogue/Voice/Keyframe/generated Video/...) are
 * separate `CanvasNode` rows with `parent_node_id` pointing here and
 * React Flow `parentId` + `extent: 'parent'` — dragging the Scene moves
 * every child with it for free (React Flow's own parent/child
 * semantics), and there is no fixed slot count: 0..N of any child type
 * is just "however many nodes currently have this `parentId`" (spec
 * §11 — never `character_1`/`character_2`). */

import { NodeResizer, type NodeProps } from '@xyflow/react'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { ClusterOutlined, DownOutlined, RightOutlined } from '@ant-design/icons'
import { useNodeDefinition } from '../nodeRegistry'
import { useCanvasNodeRegistryI18n } from '../nodeRegistryI18n'
import type { FlowNodeData } from '../types'

function SceneNodeImpl({ id, data, selected }: NodeProps) {
  const { t } = useTranslation('canvas')
  const { nodeInstanceLabel } = useCanvasNodeRegistryI18n()
  const nodeData = data as unknown as FlowNodeData
  const def = useNodeDefinition(nodeData.nodeType)
  const label = nodeInstanceLabel(nodeData.nodeType, nodeData.fields?.label as string | undefined, def.label)
  const childCount = nodeData.childCount ?? 0
  const collapseLabel = nodeData.collapsed ? t('scene.expand') : t('scene.collapse')

  return (
    <div className={`pcv-scene${selected ? ' is-selected' : ''}${nodeData.collapsed ? ' is-collapsed' : ''}`}>
      <NodeResizer isVisible={!!selected && !nodeData.collapsed} minWidth={360} minHeight={140} lineClassName="pcv-scene-resize-line" handleClassName="pcv-scene-resize-handle" />
      <div className="pcv-scene-header">
        <button
          type="button"
          className="pcv-scene-collapse-btn"
          onClick={(e) => {
            e.stopPropagation()
            nodeData.onToggleCollapsed?.(id)
          }}
          title={collapseLabel}
          aria-label={collapseLabel}
        >
          {nodeData.collapsed ? <RightOutlined /> : <DownOutlined />}
        </button>
        <ClusterOutlined className="pcv-scene-icon" />
        <span className="pcv-scene-title">{label}</span>
        <span className="pcv-scene-count">{t('scene.childCount', { count: childCount })}</span>
      </div>
      {nodeData.collapsed ? null : <div className="pcv-scene-body" />}
    </div>
  )
}

export default memo(SceneNodeImpl)
